import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSEO from '@/hooks/useSEO';
import { stats, testimonials } from '@/mocks/home';

const milestones = [
  { year: '2020', title: '平台创立', desc: '51黑料网正式上线，以"还原真相"为初心，聚焦社会热点黑料曝光' },
  { year: '2021', title: '首次独家爆料', desc: '发布首篇千万级阅读量的独家调查报告，奠定行业影响力' },
  { year: '2022', title: '团队扩张', desc: '核心调查团队扩充至30+人，覆盖金融、科技、政商、民生等领域' },
  { year: '2023', title: '累计万篇曝光', desc: '累计发布曝光文章突破10,000篇，月活跃用户突破500万' },
  { year: '2024', title: '行业认可', desc: '获年度最具影响力新媒体奖，多篇调查报道推动政策改革' },
  { year: '2025', title: '生态升级', desc: '建立完整的举报人保护机制，搭建黑料线索众包平台' },
  { year: '2026', title: '全新出发', desc: '平台全面升级，持续深耕黑料曝光领域，为用户提供更深度、更及时的真相内容' },
];

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  website_alt: string;
}

export default function About() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    website_alt: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  useSEO({
    title: '关于我们 - 51黑料网 | 品牌介绍与联系方式',
    description: '51黑料网成立于2020年，专注于曝光各类黑料、内幕、丑闻等内容。累计发布超12,000篇独家调查报道，拥有800万+月活跃用户。了解我们的品牌故事与使命，欢迎爆料投稿与合作。',
    canonicalUrl: 'http://peachbeat.cn/about',
    ogImage: 'https://readdy.ai/api/search-image?query=Dark%20investigative%20journalism%20newsroom%20with%20dramatic%20amber%20desk%20lamp%20lighting%2C%20deep%20charcoal%20shadows%2C%20cinematic%20noir%20atmosphere%2C%20professional%20editorial%20style%2C%20documents%20scattered%20on%20wooden%20desk%2C%20mysterious%20mood%20with%20subtle%20crimson%20accents&width=1200&height=630&seq=about-og-brand&orientation=landscape',
    ogType: 'website',
  });

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 51黑料网',
    description: '51黑料网成立于2020年，专注于曝光各类黑料、内幕、丑闻等内容，为公众提供真相和参考。',
    url: 'http://peachbeat.cn/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '51黑料网',
      url: 'http://peachbeat.cn/',
    },
    about: {
      '@type': 'Organization',
      name: '51黑料网',
      url: 'http://peachbeat.cn/',
      description: '2026年度黑料曝光平台，每日更新最新黑料揭秘与独家黑料曝光',
      foundingDate: '2020',
    },
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setFormError('请填写所有必填字段');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setFormError('请输入有效的邮箱地址');
      return;
    }

    if (formData.message.trim().length > 500) {
      setFormError('留言内容不能超过500个字符');
      return;
    }

    if (submitting) return;

    if (formData.website_alt.trim()) {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    try {
      const payload = new URLSearchParams();
      payload.append('name', formData.name.trim());
      payload.append('email', formData.email.trim());
      payload.append('subject', formData.subject.trim());
      payload.append('message', formData.message.trim());

      await fetch('https://readdy.ai/api/form/d9151o8bhec1he3opor0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
    } catch {
      // silently handle
    } finally {
      setSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '', website_alt: '' });
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar transparent={false} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20investigative%20newsroom%20with%20warm%20amber%20desk%20lamp%20illuminating%20scattered%20documents%2C%20cinematic%20noir%20atmosphere%2C%20deep%20charcoal%20shadows%2C%20professional%20editorial%20workspace%2C%20mysterious%20mood%20with%20subtle%20crimson%20highlights%2C%20low%20key%20photography%2C%20journalism%20theme&width=1600&height=900&seq=about-hero-2026&orientation=landscape"
            alt="51黑料网 - 关于我们"
            className="w-full h-full object-cover object-top"
            title="51黑料网 - 品牌介绍"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-background-50"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 md:px-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-500/15 border border-accent-500/25 text-accent-300 text-xs font-medium mb-5">
              <i className="ri-building-2-line text-sm"></i>
              关于我们
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-50 text-center leading-tight max-w-3xl">
              还原<strong className="text-accent-400">真相</strong>，<br className="md:hidden" />为公众<strong className="text-accent-400">发声</strong>
            </h1>
            <p className="mt-5 text-sm md:text-base text-foreground-300 text-center max-w-xl leading-relaxed">
              51黑料网成立于2020年，是国内领先的黑料曝光与内幕揭秘平台。我们致力于通过深度调查为公众还原事件真相。
            </p>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {/* Mission */}
              <div className="group bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/40 hover:border-primary-500/20 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 mb-5">
                  <i className="ri-crosshair-line text-2xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-100 mb-3">
                  我们的<strong className="text-primary-400">使命</strong>
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  以独立、客观、深度的调查报道，揭露社会各领域的黑料与内幕，为公众提供真实可靠的信息参考，推动社会向着更加公平透明的方向发展。
                </p>
              </div>

              {/* Vision */}
              <div className="group bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/40 hover:border-accent-500/20 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-400 mb-5">
                  <i className="ri-lightbulb-line text-2xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-100 mb-3">
                  我们的<strong className="text-accent-400">愿景</strong>
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  成为中国最具公信力的黑料曝光平台，让每一个普通人都有渠道了解真相、维护权益。打造全民参与的社会监督网络。
                </p>
              </div>

              {/* Values */}
              <div className="group bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/40 hover:border-secondary-500/20 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary-500/10 text-secondary-400 mb-5">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-100 mb-3">
                  我们的<strong className="text-secondary-400">价值观</strong>
                </h2>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  坚守真相底线，保护爆料人隐私安全。以事实为根基、以数据为支撑，对所有报道内容的真实性负责，绝不向任何势力妥协。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Numbers */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50">
                用数据<strong className="text-accent-500">说话</strong>
              </h2>
              <p className="mt-3 text-sm text-foreground-400 max-w-lg mx-auto">
                6年来，51黑料网始终坚持独立调查，用一组组数据证明我们的价值
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <strong className="text-3xl md:text-4xl font-heading font-bold text-accent-500">
                    {stat.value}
                  </strong>
                  <div className="mt-2 text-xs md:text-sm text-foreground-500">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones Timeline */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50">
                发展<strong className="text-primary-500">历程</strong>
              </h2>
              <p className="mt-3 text-sm text-foreground-400">
                从创立到行业领先，每一步都离不开用户的支持
              </p>
            </div>
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((milestone, idx) => (
                  <div
                    key={milestone.year}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Dot */}
                    <div className="absolute left-3.5 md:left-1/2 top-1 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 md:-translate-x-1.5 z-10"></div>

                    {/* Content */}
                    <div className={`ml-10 md:ml-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <span className="inline-block px-3 py-0.5 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium mb-2">
                        {milestone.year}
                      </span>
                      <h3 className="text-base font-heading font-bold text-foreground-100 mb-1.5">
                        {milestone.title}
                      </h3>
                      <p className="text-sm text-foreground-400 leading-relaxed">
                        {milestone.desc}
                      </p>
                    </div>
                    {/* Empty spacer for alternating layout */}
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-500/10 border border-secondary-500/20 text-secondary-400 text-xs font-medium mb-4">
                <i className="ri-chat-quote-line text-sm"></i>
                用户口碑
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50">
                他们这样<strong className="text-secondary-500">评价</strong>我们
              </h2>
              <p className="mt-3 text-sm text-foreground-400 max-w-lg mx-auto">
                来自用户、媒体人、专家的真实反馈，是对我们最大的认可
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-50 rounded-lg p-6 md:p-7 border border-background-200/40 flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                        title={t.name}
                      />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-100">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <blockquote className="text-sm text-foreground-300 leading-relaxed flex-1 italic">
                    &ldquo;{t.content}&rdquo;
                  </blockquote>
                  <div className="flex items-center gap-1 mt-4 text-accent-400">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-xs font-medium mb-4">
                <i className="ri-mail-send-line text-sm"></i>
                联系我们
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50">
                有料要<strong className="text-primary-500">爆</strong>？联系我们
              </h2>
              <p className="mt-3 text-sm text-foreground-400 max-w-lg mx-auto">
                如果您掌握黑料线索或有任何投诉建议，欢迎通过以下表单联系我们。我们承诺对爆料人信息严格保密。
              </p>
            </div>

            {submitted ? (
              <div className="bg-background-100 rounded-lg border border-background-200/40 p-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500/10 text-accent-400 mx-auto mb-5">
                  <i className="ri-check-line text-3xl"></i>
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground-100 mb-2">提交成功！</h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  感谢您的来信。我们的团队会在24小时内查收并处理，如有必要会通过您留下的联系方式与您取得联系。
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-background-100 rounded-lg border border-background-200/40 p-6 md:p-8"
                data-readdy-form=""
              >
                {/* Honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="name" className="text-sm text-foreground-300 font-medium">
                      姓名 <span className="text-primary-400">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="您的姓名"
                      required
                      className="px-4 py-3 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/60 transition-colors duration-200"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-sm text-foreground-300 font-medium">
                      邮箱 <span className="text-primary-400">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="px-4 py-3 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/60 transition-colors duration-200"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2 mt-5">
                  <label htmlFor="subject" className="text-sm text-foreground-300 font-medium">
                    主题
                  </label>
                  <input
                    id="subject"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="如：爆料投稿 / 合作咨询 / 投诉建议"
                    className="px-4 py-3 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/60 transition-colors duration-200"
                  />
                </div>

                <div className="flex flex-col gap-2 mt-5">
                  <label htmlFor="message" className="text-sm text-foreground-300 font-medium">
                    留言内容 <span className="text-primary-400">*</span>
                    <span className="text-foreground-600 ml-2 font-normal">（最多500字）</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="请详细描述您的爆料内容或咨询事项..."
                    required
                    rows={6}
                    maxLength={500}
                    className="px-4 py-3 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/60 transition-colors duration-200 resize-none"
                  ></textarea>
                  <div className="text-xs text-foreground-600 text-right">
                    {formData.message.length}/500
                  </div>
                </div>

                {formError && (
                  <div className="mt-4 px-4 py-3 rounded-md bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </div>
                )}

                <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-foreground-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>提交中...</>
                    ) : (
                      <>
                        提交留言
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>
                  <span className="text-xs text-foreground-500">
                    提交即表示您同意我们的隐私政策
                  </span>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100">
              您的声音就是我们的<strong className="text-primary-500">力量</strong>
            </h2>
            <p className="mt-3 text-sm text-foreground-400 leading-relaxed">
              每一份爆料、每一条线索，都可能成为推动社会进步的关键。51黑料网始终与真相同行。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
              <Link
                to="/"
                className="px-6 py-3 bg-primary-500 text-foreground-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                回到首页
              </Link>
              <Link
                to="/category/exclusive"
                className="px-6 py-3 border border-background-300/60 rounded-full text-sm text-foreground-300 hover:text-foreground-100 hover:border-foreground-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                浏览独家黑料
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
